import React, { useEffect, useState, useCallback } from "react";
import { Receipt, ShoppingBag, PackageCheck, Truck, Settings2 } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Select } from "../lib/ui";
import { fmtNum, fmtUnit, fmtRp } from "../lib/constants";
import { useT } from "../lib/i18n";

const TX_META = {
  Purchase: { icon: ShoppingBag, tone: "success" },
  Receiving: { icon: PackageCheck, tone: "info" },
  "Transfer Out": { icon: Truck, tone: "warn" },
  Adjustment: { icon: Settings2, tone: "mute" },
};

const TX_FILTERS = ["all", "Purchase", "Receiving", "Transfer Out", "Adjustment"];
const TX_LABEL = { all: "All", Purchase: "Purchase", Receiving: "Receiving", "Transfer Out": "Transfer Out", Adjustment: "Adjustment" };

const todayStr = () => new Date().toISOString().slice(0, 10);
const shiftDays = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function CostLedger() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [txType, setTxType] = useState("all");
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());

  const applyPeriod = (p) => {
    setPeriod(p);
    if (p === "today") { setFrom(todayStr()); setTo(todayStr()); }
    else if (p === "week") { setFrom(shiftDays(6)); setTo(todayStr()); }
    else if (p === "month") { setFrom(monthStart()); setTo(todayStr()); }
  };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (txType !== "all") params.set("tx_type", txType);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    setData(null);
    api.get(`/cost-ledger?${params.toString()}`).then(({ data }) => setData(data));
  }, [txType, from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Cost Ledger")} subtitle={t("Inventory cost transaction history — central warehouse")} icon={Receipt} />

      <div className="mb-3 flex flex-wrap gap-2">
        {TX_FILTERS.map((k) => (
          <button key={k} onClick={() => setTxType(k)} data-testid={`costledger-filter-${k.replace(/\s/g, "-")}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${txType === k ? "bg-primary text-white" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
            {t(TX_LABEL[k])}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="inline-flex overflow-hidden rounded-xl border border-line">
          {["today", "week", "month"].map((p) => (
            <button key={p} onClick={() => applyPeriod(p)} data-testid={`costledger-period-${p}`}
              className={`px-3 py-2 text-xs font-semibold capitalize transition-colors ${period === p ? "bg-primary text-white" : "bg-white text-ink-soft hover:bg-stone-50"}`}>
              {p === "week" ? t("This Week") : p === "month" ? t("This Month") : t("Today")}
            </button>
          ))}
        </div>
        <div><label className="cf-label">{t("From")}</label><input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }} className="cf-input" data-testid="costledger-from" /></div>
        <div><label className="cf-label">{t("To")}</label><input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }} className="cf-input" data-testid="costledger-to" /></div>
      </div>

      {!data ? <PageLoader /> : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Cost In")}</p><p className="mt-1 text-xl font-bold text-success-text" data-testid="costledger-in">{fmtRp(data.totals.in_value)}</p></Card>
            <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Cost Out")}</p><p className="mt-1 text-xl font-bold text-warn-text" data-testid="costledger-out">{fmtRp(data.totals.out_value)}</p></Card>
            <Card className="col-span-2 p-4 sm:col-span-1"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Transactions")}</p><p className="mt-1 text-xl font-bold text-ink">{data.totals.count}</p></Card>
          </div>

          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <th className="px-4 py-3">{t("When")}</th><th className="px-4 py-3">{t("Type")}</th>
                  <th className="px-4 py-3">{t("Product")}</th><th className="px-4 py-3 text-right">{t("Qty")}</th>
                  <th className="px-4 py-3 text-right">{t("Unit Cost")}</th><th className="px-4 py-3 text-right">{t("Total Cost")}</th>
                  <th className="hidden px-4 py-3 sm:table-cell">{t("Reference")}</th></tr></thead>
                <tbody>
                  {data.rows.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-ink-mute">{t("No transactions in this range")}</td></tr>}
                  {data.rows.map((r) => {
                    const meta = TX_META[r.tx_type] || { icon: Settings2, tone: "mute" };
                    const Icon = meta.icon;
                    return (
                      <tr key={r.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid="costledger-row">
                        <td className="px-4 py-3 text-xs text-ink-mute">{(r.date || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-3"><Badge tone={meta.tone}><Icon size={13} /> {t(r.tx_type)}</Badge></td>
                        <td className="px-4 py-3 font-semibold">{r.product_name}</td>
                        <td className={`px-4 py-3 text-right font-bold ${r.direction === "in" ? "text-success-text" : "text-warn-text"}`}>{r.direction === "in" ? "+" : "−"}{fmtNum(r.quantity)} {fmtUnit(r.unit)}</td>
                        <td className="px-4 py-3 text-right text-ink-soft">{fmtRp(r.unit_cost)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-ink">{fmtRp(r.total_cost)}</td>
                        <td className="hidden px-4 py-3 font-mono text-xs text-ink-soft sm:table-cell">{r.reference_no || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
